/**
 * Hook useAgGridColumns para Returned/List
 *
 * Define las columnas de AG Grid para la tabla de Recepciones.
 * Incluye cellRenderers personalizados y genera columnas TanStack para el ColumnSelector.
 */

import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ReturnedModel } from '../models/Returned';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Button } from '@shared/components/ui/button';
import { Link } from 'react-router-dom';
import {
  getReturnedStatusName,
  getReturnedStatusBadgeClass,
  getReturnedActionButtonText,
  shouldShowReturnedActionButton,
  getReturnedActionType,
} from '../config';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy HH:mm
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

// 🎯 Función para convertir ColDef de AG Grid a ColumnDef de TanStack para ColumnSelector
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<ReturnedModel>[] => {
  return agGridColumns
    .filter((col) => {
      // 🎯 Excluir columnas que no deben ser opcionales
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actionsReturned' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
      cell: col.cellRenderer ? () => null : undefined, // ColumnSelector no necesita cell renderers
    }));
};

// 🎯 Tipos para los handlers
interface UseAgGridColumnsProps {
  selectedColumns?: string[];
}

// 🎯 Hook para manejar las columnas de AG Grid y ColumnSelector
export const useAgGridColumns = ({ selectedColumns = [] }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('returnedModule');

  // 🎯 Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: Código (code)
      {
        field: 'code',
        headerName: t('table.code'),
        sortable: false,
        // 🎯 CELDA: Muestra el código con formato destacado
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedModel;
          return <span className="text-sm font-medium text-title">{item.code}</span>;
        },
      },

      // 🔵 COLUMNA: Estado (status)
      {
        field: 'status',
        headerName: t('table.status_label'),
        sortable: false,
        valueGetter: (params) => getReturnedStatusName(params.data.status, t),
        // 🎯 CELDA: Badge con color según estado
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedModel;
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getReturnedStatusBadgeClass(item.status)}`}
            >
              {getReturnedStatusName(item.status, t)}
            </span>
          );
        },
      },

      // 🔵 COLUMNA: Fecha de creación (created_at)
      {
        field: 'created_at',
        headerName: t('table.date'),
        sortable: false,
        // 🎯 CELDA: Fecha formateada
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedModel;
          return <span className="text-sm">{formatDate(item.created_at)}</span>;
        },
      },

      // 🔵 COLUMNA: Cantidad de pedidos (orders_count)
      {
        field: 'orders_count',
        headerName: t('table.orders'),
        sortable: false,
        // 🎯 CELDA: Número de pedidos
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedModel;
          return <span className="text-sm font-medium">{item.orders_count}</span>;
        },
      },
    ];

    // 🔵 COLUMNA: Acciones
    baseColumns.push({
      headerName: '',
      field: 'actionsReturned',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      resizable: false,
      // 🔒 Reglas críticas: acciones fijadas a la derecha y no movibles
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      sortable: false,
      // 🎯 CELDA: Botón de acción según el estado
      cellRenderer: (params: any) => {
        const item = params.data as ReturnedModel;
        const actionType = getReturnedActionType(item.status);

        if (!shouldShowReturnedActionButton(item.status)) {
          return null;
        }

        return (
          <div className="flex justify-center w-full">
            <Link
              to={`/returned/${item.id}/receive`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs w-full"
              data-testid={`${actionType}-returned-button`}
            >
              <Button type="button" variant="outline" size="sm" className="w-full">
                {getReturnedActionButtonText(item.status, t)}
              </Button>
            </Link>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t]);

  // 🎯 Filtrar columnas basado en la selección del ColumnSelector
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // 🎯 Columnas que siempre deben estar visibles
    const alwaysVisibleColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      // Mantener siempre 'actions' y 'code' para evitar estados sin columnas de datos
      return field === 'actionsReturned' || field === 'code' || field === 'unknown';
    });

    // 🎯 Columnas seleccionables
    const selectableColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actionsReturned' && field !== 'unknown';
    });

    // Utilidad para evitar columnas duplicadas por `field`
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };

    if (selectedColumns.length === 0) {
      // Si no hay selección, mostrar todas las columnas seleccionables + las siempre visibles (sin duplicados)
      return dedupeByField([...selectableColumns, ...alwaysVisibleColumns]);
    }

    // Filtrar columnas seleccionables basado en la selección
    const selectedSelectableColumns = selectableColumns.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      return selectedColumns.includes(fieldId);
    });

    // Combinar columnas seleccionadas + siempre visibles SIN duplicados
    const combined = [...selectedSelectableColumns];
    alwaysVisibleColumns.forEach((c) => {
      const field = c.field || c.headerName || 'unknown';
      const exists = combined.some((x) => (x.field || x.headerName || 'unknown') === field);
      if (!exists) combined.push(c);
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Columnas para el ColumnSelector (formato TanStack)
  const tanStackColumns: ColumnDef<ReturnedModel>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
